import { useQuery } from '@apollo/client/react'
import { Box, HStack, Text, VStack } from '@chakra-ui/react'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FiChevronDown } from 'react-icons/fi'
import { toast } from 'react-toastify'

import { useAuth } from '@/auth/useAuth'
import { UserMenuContent } from '@/components/molecules/Menu/UserMenuContent'
import { getAuthorizedOrderBySlug } from '@/components/molecules/Menu/UserMenuContent'
import { useUserMenuContextSwitchState } from '@/components/molecules/Menu/UserMenuContent/hooks/useUserMenuContextSwitchState'
import { ContextSwitchSection } from '@/components/molecules/Menu/UserMenuContent/sections/ContextSwitchSection'
import { CircleLogo } from '@/components/molecules/User/Circle/CircleLogo'
import { NetworkLogo } from '@/components/molecules/User/Network/NetworkLogo'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { useColorModeValue } from '@/components/ui/color-mode'
import { MenuContent, MenuRoot, MenuTrigger } from '@/components/ui/menu'
import {
  GetUserNetworksForContextMenuDocument,
  GetUserNetworksForContextMenuQuery,
  MyCirclesDocument,
  MyCirclesQuery,
} from '@/graphql/api/generated'
import { GET_USER_NETWORKS_USER_MENU_VARIABLES } from '@/graphql/getUserNetworksUserMenuVariables'
import { useContext } from '@/hooks/useContext'
import { useContextSwitchDispatcher } from '@/hooks/useContextSwitchDispatcher'
import { useSwitchContext } from '@/hooks/useSwitchContext'
import { useTracking } from '@/hooks/useTracking'

import { useEndImpersonation } from '../Table/User/useEndImpersonation'

export interface UserMenuProps {
  /**
   * `rail` is the sidebar's head: a wide trigger naming the current context, opening the same
   * switcher as the avatar in the top bar. It shows only the switcher — the destinations that
   * the dropdown lists are, in the rail, the rows immediately below it.
   */
  variant?: 'dropdown' | 'drawer' | 'rail'
  onClose?: () => void
  /** `rail` only: the sidebar is narrow, so the head is reduced to the logo. */
  collapsed?: boolean
}

export const UserMenu = ({
  variant = 'dropdown',
  onClose,
  collapsed = false,
}: UserMenuProps = {}) => {
  const { t } = useTranslation()
  const { user, isImpersonationExchangeSession } = useAuth()
  const { captureEvent } = useTracking()
  const { endImpersonation } = useEndImpersonation()
  const { contextIri, contextName, isCircle, isNetwork } = useContext()
  const { switchContext, loading } = useSwitchContext()
  const [hoveredCircleId, setHoveredCircleId] = useState<string | null>(null)
  const bgAccent = useColorModeValue('gray.100', 'gray.800')
  const badgeBg = useColorModeValue('gray.800', 'gray.700')
  const { dispatchContextSwitchNavigation } = useContextSwitchDispatcher()

  // Get circle data if context is a circle
  const { data: circlesData, loading: circlesLoading } =
    useQuery<MyCirclesQuery>(MyCirclesDocument, {
      skip: !user,
    })

  const activeCircle = circlesData?.myCircles?.edges
    ?.map((edge) => edge?.node)
    .find((circle) => circle?.id === contextIri)

  const allCircles =
    circlesData?.myCircles?.edges
      ?.map((edge) => edge?.node)
      .filter((node) => node !== undefined) || []

  const authorizedContextsTree =
    user?.authorizedContextsTree ?? user?.treeCircles
  const authorizedOrderBySlug = useMemo(
    () => getAuthorizedOrderBySlug(authorizedContextsTree),
    [authorizedContextsTree]
  )

  const circles = useMemo(() => {
    const inTree = allCircles.filter((circle) =>
      authorizedOrderBySlug.has(circle.slug)
    )
    const notYetInTree = allCircles.filter(
      (circle) => !authorizedOrderBySlug.has(circle.slug)
    )

    const sortedInTree = [...inTree].sort((a, b) => {
      const leftOrder = authorizedOrderBySlug.get(a.slug) ?? 0
      const rightOrder = authorizedOrderBySlug.get(b.slug) ?? 0
      if (leftOrder !== rightOrder) return leftOrder - rightOrder
      return a.name.localeCompare(b.name)
    })
    const sortedOrphans = [...notYetInTree].sort((a, b) =>
      a.name.localeCompare(b.name)
    )

    return [...sortedInTree, ...sortedOrphans]
  }, [allCircles, authorizedOrderBySlug])

  const { data: networksData } = useQuery<GetUserNetworksForContextMenuQuery>(
    GetUserNetworksForContextMenuDocument,
    {
      skip: !user,
      variables: GET_USER_NETWORKS_USER_MENU_VARIABLES,
    }
  )

  const networks = useMemo(() => {
    const allNetworks =
      networksData?.myNetworks?.edges
        ?.map((edge) => edge?.node)
        .filter(
          (node): node is NonNullable<typeof node> =>
            node != null && node.deletedAt == null
        ) || []

    const inTree = allNetworks.filter((network) =>
      authorizedOrderBySlug.has(network.slug)
    )
    const notYetInTree = allNetworks.filter(
      (network) => !authorizedOrderBySlug.has(network.slug)
    )

    const sortedInTree = [...inTree].sort((a, b) => {
      const leftOrder = authorizedOrderBySlug.get(a.slug) ?? 0
      const rightOrder = authorizedOrderBySlug.get(b.slug) ?? 0
      if (leftOrder !== rightOrder) return leftOrder - rightOrder
      return a.name.localeCompare(b.name)
    })
    const sortedOrphans = [...notYetInTree].sort((a, b) =>
      a.name.localeCompare(b.name)
    )

    return [...sortedInTree, ...sortedOrphans]
  }, [networksData?.myNetworks?.edges, authorizedOrderBySlug])

  const activeNetwork = networks.find((network) => network?.id === contextIri)

  // Determine display name
  const displayName =
    (isCircle || isNetwork) && contextName ? contextName : user?.firstname

  const handleSwitchContext = useCallback(
    async (targetIri: string | null, targetName?: string | null) => {
      try {
        // Ensure we have a name to display in the overlay
        const displayName = targetIri
          ? targetName ||
            circles.find((c) => c?.id === targetIri)?.name ||
            networks.find((n) => n?.id === targetIri)?.name ||
            targetIri
          : user?.firstname || t('context.user')

        const targetCircle = targetIri
          ? circles.find((c) => c?.id === targetIri)
          : null
        const targetNetwork = targetIri
          ? networks.find((n) => n?.id === targetIri)
          : null

        const result = await switchContext(targetIri, displayName)
        if (result.success) {
          // Create custom icon element (larger size)
          // Use CircleLogo for circles (handles fallback to community icon)
          // Use Avatar for user context
          const customIcon =
            targetIri && targetCircle ? (
              <CircleLogo circle={targetCircle} size="lg" />
            ) : targetIri && targetNetwork ? (
              <NetworkLogo network={targetNetwork} size="lg" />
            ) : (
              <Avatar
                size="lg"
                src={user?.avatar?.contentUrl || undefined}
                name={result.contextName || ''}
                flexShrink={0}
              />
            )

          // Create custom toast with avatar/logo (displays immediately after dialog closes)
          toast.info(
            <HStack gap={3} alignItems="center" as="div">
              {customIcon}
              <Text fontSize="sm" as="span">
                {t('context.switched', {
                  contextName: result.contextName || t('context.user'),
                })}
              </Text>
            </HStack>,
            {
              icon: false, // Disable default icon to avoid duplication
            }
          )
        }

        if (result.success) {
          dispatchContextSwitchNavigation(targetIri)
        }
      } catch (error) {
        toast.error(t('context.switchError'))
        console.error('Failed to switch context:', error)
      }
    },
    [switchContext, t, circles, networks, user, dispatchContextSwitchNavigation]
  )
  const borderColor = useColorModeValue('white', 'gray.900')
  const switchState = useUserMenuContextSwitchState({ circles, networks, user })

  if (!user) return null

  const contentProps = {
    asDrawer: variant === 'drawer',
    onClose: variant === 'drawer' ? onClose : undefined,
    user,
    circles,
    networks,
    activeCircle,
    activeNetwork,
    isCircle,
    isNetwork,
    contextIri,
    contextName,
    loading,
    circlesLoading,
    hoveredCircleId,
    setHoveredCircleId,
    endImpersonation,
    isImpersonating: isImpersonationExchangeSession,
    captureEvent,
    t,
    handleSwitchContext,
  }

  if (variant === 'rail') {
    const kind = isCircle
      ? t('context.circle')
      : isNetwork
        ? t('context.network')
        : t('context.personal')

    const visual =
      isCircle && activeCircle ? (
        <CircleLogo circle={activeCircle} size="sm" />
      ) : isNetwork && activeNetwork ? (
        <NetworkLogo network={activeNetwork} size="sm" />
      ) : (
        <Avatar
          size="sm"
          src={user?.avatar?.contentUrl || undefined}
          name={displayName || undefined}
          aria-label={`avatar ${displayName || user?.fullname}`}
        />
      )

    return (
      <MenuRoot variant="subtle">
        {/* A real <button>: `asChild` on a Box leaves the trigger without a role, so neither a
            screen reader nor a keyboard could reach the switcher. */}
        <MenuTrigger asChild cursor="pointer">
          <Button
            unstyled
            display="flex"
            alignItems="center"
            gap={2.5}
            w="full"
            px={collapsed ? 2 : 3}
            py={2}
            minH="48px"
            borderRadius="lg"
            borderWidth="1px"
            borderColor="border"
            justifyContent={collapsed ? 'center' : 'flex-start'}
            _hover={{ bg: bgAccent }}
            data-tour="global-context-switch-trigger"
            aria-label={`${kind} — ${displayName ?? ''}`}
          >
            <Box flexShrink={0}>{visual}</Box>
            {collapsed ? null : (
              <>
                <VStack align="stretch" gap={0} flex="1" minW={0}>
                  <Text fontSize="sm" fontWeight="semibold" truncate>
                    {displayName}
                  </Text>
                  <Text fontSize="11px" color="fg.subtle" truncate>
                    {kind}
                  </Text>
                </VStack>
                <FiChevronDown size={14} />
              </>
            )}
          </Button>
        </MenuTrigger>
        <MenuContent bg={bgAccent} minW="320px">
          <ContextSwitchSection
            mode="dropdown"
            rowPx={4}
            rowPy={2}
            switchState={switchState}
            user={user}
            circles={circles}
            networks={networks}
            contextIri={contextIri}
            loading={loading}
            circlesLoading={circlesLoading}
            hoveredCircleId={hoveredCircleId}
            setHoveredCircleId={setHoveredCircleId}
            handleSwitchContext={handleSwitchContext}
            t={t}
          />
        </MenuContent>
      </MenuRoot>
    )
  }

  if (variant === 'drawer') {
    return (
      <VStack align="stretch" gap={0}>
        <HStack p={4} gap={3}>
          {isCircle && activeCircle ? (
            <CircleLogo circle={activeCircle} size="sm" />
          ) : isNetwork && activeNetwork ? (
            <NetworkLogo network={activeNetwork} size="sm" />
          ) : (
            <Avatar
              size="sm"
              src={user?.avatar?.contentUrl || undefined}
              name={displayName || undefined}
              aria-label={`avatar ${displayName || user?.fullname}`}
            />
          )}
          <Text fontWeight="medium">{displayName}</Text>
        </HStack>
        <UserMenuContent {...contentProps} />
      </VStack>
    )
  }

  return (
    <MenuRoot variant="subtle">
      <MenuTrigger asChild cursor="pointer">
        <Box
          position="relative"
          display="inline-block"
          data-tour="global-context-switch-trigger"
        >
          {isCircle && activeCircle ? (
            <CircleLogo circle={activeCircle} size="sm" />
          ) : isNetwork && activeNetwork ? (
            <NetworkLogo network={activeNetwork} size="sm" />
          ) : (
            <Avatar
              size="sm"
              src={user?.avatar?.contentUrl || undefined}
              aria-label={`avatar ${displayName || user?.fullname}`}
            />
          )}
          <Box
            position="absolute"
            bottom="0"
            right="0"
            bg={badgeBg}
            borderRadius="full"
            p="2px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderWidth="2px"
            borderColor={borderColor}
            transform="translate(25%, 25%)"
          >
            <FiChevronDown size={10} color="white" />
          </Box>
        </Box>
      </MenuTrigger>
      <MenuContent bg={bgAccent} minW="360px">
        <UserMenuContent {...contentProps} />
      </MenuContent>
      <style>
        {`
          @keyframes rotate-arrows {
            from {
              transform: translate(-50%, -50%) rotate(0deg);
            }
            to {
              transform: translate(-50%, -50%) rotate(360deg);
            }
          }
        `}
      </style>
    </MenuRoot>
  )
}
